import React from 'react'
import { DateTime } from 'luxon'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import EventIcon from 'components/material-symbol/icons/Event'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from 'components/dialog'
import { REMOVE_COACH_AVAILABILITY_OVERRIDE } from './queries'

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  detailsCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#E3F2FD',
    border: '1px solid #90CAF9',
    borderRadius: 8,
  },
  detailsIcon: {
    color: '#1565C0',
  },
  detailsTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  detailsDate: {
    fontWeight: 600,
    lineHeight: 1.3,
  },
  detailsTime: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}))

const CustomAvailabilityDestroyDialog = ({ override, onSuccess, trigger }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const formattedDate = DateTime.fromISO(override.date).toFormat(
    'EEE, MMM d, yyyy',
  )
  const formatSlotTime = iso => {
    const dt = DateTime.fromISO(iso)
    return dt.minute === 0 ? dt.toFormat('h a') : dt.toFormat('h:mm a')
  }
  const formattedSlots = override.timeSlots
    .map(
      ts => `${formatSlotTime(ts.startTime)} – ${formatSlotTime(ts.endTime)}`,
    )
    .join(', ')

  const [removeOverride] = useMutation(REMOVE_COACH_AVAILABILITY_OVERRIDE)

  const handleRemoveOverride = closeDialog => {
    removeOverride({
      variables: { id: override.id },
      onCompleted: data => {
        if (data?.removeCoachAvailabilityOverride?.success) {
          enqueueSnackbar('One-time availability deleted', {
            variant: 'success',
          })
          onSuccess && onSuccess()
          closeDialog()
        } else {
          enqueueSnackbar(
            data?.removeCoachAvailabilityOverride?.message ||
              'Failed to delete one-time availability',
            { variant: 'error' },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          'Failed to delete one-time availability. Please try again.',
          { variant: 'error' },
        )
      },
    })
  }

  return (
    <Dialog trigger={trigger} maxWidth="sm" fullWidth showCloseButton>
      {({ closeDialog }) => (
        <>
          <DialogTitle className={classes.header}>
            Delete one-time availability?
          </DialogTitle>
          <DialogContent dividers>
            <Box py={2}>
              <Box mb={2}>
                <Typography>
                  Deleting this one-time availability will revert to your
                  general availability for this date.
                </Typography>
              </Box>

              <Box className={classes.detailsCard} mb={2}>
                <EventIcon className={classes.detailsIcon} fontSize="small" />
                <Box className={classes.detailsTextCol}>
                  <Typography variant="body2" className={classes.detailsDate}>
                    {formattedDate}
                  </Typography>
                  <Typography variant="body2" className={classes.detailsTime}>
                    {formattedSlots}
                  </Typography>
                </Box>
              </Box>

              <Box mb={2}>
                <Typography>Are you sure you want to delete it?</Typography>
              </Box>

              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button onClick={closeDialog} size="large">
                  Cancel
                </Button>
                <Box ml={2}>
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.deleteButton}
                    onClick={() => handleRemoveOverride(closeDialog)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export default CustomAvailabilityDestroyDialog
